/** @jsx jsx */
import { jsx, useThemeUI } from 'theme-ui';
import { Themed } from '@theme-ui/mdx';
import {
  CarouselProvider,
  Slider,
  Slide,
  DotGroup,
  ButtonBack,
  ButtonNext,
  CarouselContext,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { ChevronIcon } from './Icon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

const RewardCarousel = ({ title, cards = [], containerSx = {}, ...props }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const [isMounted, setIsMounted] = useState(false);
  const { width: windowWidth = 768 } = useWindowDimensions();

  useEffect(() => {
    // SSR does not work well with changing number of cards / screen width is unknown
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const width = containerWidth ?? windowWidth - 32;

  if (!cards.length || !width) return null;

  const visibleSlides = width >= 768 ? 3 : 2;
  const cardMargin = width >= 768 ? 16 : 8;
  const cardWidth = Math.floor(
    (Math.min(width + cardMargin, 1248) - (visibleSlides - 1) * 2 * cardMargin) / visibleSlides
  );
  const gap = `${cardMargin / 2}px`;

  if (!cards?.length) return null;
  return (
    <div ref={containerRef} sx={containerSx}>
      <Themed.h2 as="h2" sx={{ mb: 3 }}>
        {title}
      </Themed.h2>

      {isMounted ? (
        <div sx={{ width: visibleSlides * cardWidth + (visibleSlides - 1) * 2 * cardMargin, mx: '-' + gap }}>
          <CarouselProvider
            naturalSlideWidth={cardWidth}
            naturalSlideHeight={1.5 * cardWidth}
            totalSlides={cards.length}
            visibleSlides={Math.min(visibleSlides, cards.length)}
            isIntrinsicHeight
            infinite
            // isPlaying
            lockOnWindowScroll
            sx={{
              position: 'relative',
            }}
            {...props}
          >
            <Carousel shownCards={cards} px={gap} />
          </CarouselProvider>
        </div>
      ) : (
        <div sx={{ aspectRatio: 2.5 }}/>
      )}
    </div>
  );
};

const Carousel = ({ shownCards = [], px = '8px' }) => {
  const { theme: themeUI } = useThemeUI();
  const carouselContext = useContext(CarouselContext);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const onChange = () => {
      setCurrentSlide(carouselContext.state.currentSlide);
    };
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);

  return (
    <>
      <Slider sx={{}}>
        {shownCards?.map((card, i) => (
          <Slide index={i} sx={{ px }}>
            <RewardCard key={i} card={card} />
          </Slide>
        ))}
      </Slider>
      <ButtonControls currentSlide={currentSlide} totalSlides={shownCards?.length} />
      <DotGroup
        showAsSelectedForCurrentSlideOnly={true}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mt: 3,
          '.carousel__dot': {
            borderRadius: '50%',
            bg: 'white',
            p: '7px',
            border: `1px solid ${themeUI.colors.primary}`,
            outline: 0,
          },
          '.carousel__dot--selected': {
            bg: 'primary',
            border: `1px solid ${themeUI.colors.primary}`,
          },
        }}
      />
    </>
  );
};

const ButtonControls = ({ currentSlide, totalSlides = 0 }) => {
  // show only on mobile
  return (
    <div sx={{ display: ['block', null, null, 'none'] }}>
      {currentSlide !== 0 ? (
        <div
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            mt: '-16px',
            ml: -3,
            height: '100%',
            display: 'flex',
            flexDirection: 'col',
            alignItems: 'center',
          }}
        >
          <ButtonBack
            sx={{
              variant: 'buttons.plain',
              height: '40px',
              width: '40px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Add shadow
              borderRadius: '50%',
              backgroundColor: 'white',
              color: 'primary',
            }}
          >
            <ChevronIcon sx={{ mt: -1, ml: 2, transform: 'rotate(180deg)' }} />
          </ButtonBack>
        </div>
      ) : null}

      {currentSlide < totalSlides - 1 ? (
        <div
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            mt: '-16px',
            mr: -3,
            height: '100%',
            display: 'flex',
            flexDirection: 'col',
            alignItems: 'center',
          }}
        >
          <ButtonNext
            sx={{
              variant: 'buttons.plain',
              height: '40px',
              width: '40px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Add shadow
              borderRadius: '50%',
              backgroundColor: 'white',
              color: 'primary',
            }}
          >
            <ChevronIcon sx={{ mt: -1, ml: 2 }} />
          </ButtonNext>
        </div>
      ) : null}
    </div>
  );
};

export default RewardCarousel;

const BALL_SIZE = [48, null, 72];
const BALL_SIZE_P8 = [52, null, 80];
const LOGO_PADDING = ['4px', null, '6px']; // 1/12 * [48,,72]

const RewardCard = ({ card }) => {
  const onClick = () => window.location.replace('https://paketit.matkahuolto.fi/pisteet');

  return (
    <div
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        flex: 1,
        display: 'flex',
        gap: '8px',
        py: 2,
        px: [2, null, 3],
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden',
        bg: 'blueLighter',
        ':hover': onClick ? { opacity: 0.85 } : {},
      }}
    >
      <div
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: ['8px', null, '16px'],
          height: BALL_SIZE_P8,
          alignItems: 'center',
        }}
      >
        {card.logo && (
          <div
            sx={{
              width: BALL_SIZE,
              height: BALL_SIZE,
              minWidth: BALL_SIZE,
              bg: 'white',
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: LOGO_PADDING,
            }}
          >
            <GatsbyImage image={getImage(card.logo)} alt={card?.webShop || ''} />
          </div>
        )}
        {card.title && (
          <div sx={{ flex: 1, overflow: 'hidden' }}>
            <p
              sx={{
                fontSize: [2, null, 3],
                fontWeight: 'bold',
                textAlign: 'left',
                color: 'primary',
                m: 0,
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'pre-line',
              }}
            >
              {card.title.replace(/  +/g, '\n')}
            </p>
          </div>
        )}
      </div>
      <div sx={{ aspectRatio: '2 / 1', overflow: 'hidden' }}>
        {card.image ? (
          <GatsbyImage
            image={getImage(card.image)}
            style={{ width: '100%', height: '100%' }}
            imgStyle={{ objectFit: 'cover' }}
          />
        ) : (
          <div sx={{ width: '100%', height: '100%' }} />
        )}
      </div>
      {card.cardText && (
        <p
          sx={{
            fontSize: [17, null, 19],
            mb: 0,
            mt: 0,
            color: 'primary',
            textAlign: 'left',
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'pre-line',
          }}
        >
          {card.cardText.replace(/  +/g, '\n')}
        </p>
      )}
    </div>
  );
};
